import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ContentChildren,
  QueryList,
  NgZone,
  AfterContentInit,
  OnDestroy,
  Renderer2,
  Inject,
  forwardRef,
  ViewChild,
} from '@angular/core';
import {
  DropdownOptionComponent,
  DropdownValue,
  SelectionChange,
} from '../dropdown-option/dropdown-option.component';
import { Observable, defer, merge, Subject } from 'rxjs';
import { startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { getKeyCode, MOUSE_BUTTON } from '@cigna/ui/utils';
import { CignaIcon } from '@cigna/ui/icon';

@Component({
  selector: 'cigna-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
})
export class DropdownComponent implements AfterContentInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private listenerFns: Array<() => void> = [];
  public isOpen = false;
  public selected: DropdownOptionComponent;
  public id = Math.random().toString(36).substring(2);
  private _value: DropdownValue;

  @ContentChildren(forwardRef(() => DropdownOptionComponent), {
    descendants: true,
  })
  options: QueryList<DropdownOptionComponent>;

  @Input() placeholder: string;

  @Input()
  set value(value: DropdownValue) {
    this._value = value;
    if (this.options) {
      this.updateSelectedOption(value);
    }
  }
  get value(): DropdownValue {
    return this._value;
  }

  @Output() valueChange = new EventEmitter();
  @Input() type: 'dropdown' | 'borderless' = 'dropdown';
  @Input() isBoldedBlue?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @Input() disabled = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  @Input() leftAlign = false;
  @Input() expandIcon?: CignaIcon;

  @Output() readonly selectionChange = new EventEmitter();
  @Output() readonly viewValueChange = new EventEmitter<string>();

  @ViewChild('dropdownTrigger', { read: ElementRef, static: false })
  dropdownTrigger: ElementRef<HTMLButtonElement>;

  @ViewChild('listbox') listbox: ElementRef;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _ngZone: NgZone,
    public _elementRef: ElementRef,
    private _renderer: Renderer2,
  ) {
    this.listenerFns.push(
      this._renderer.listen(this._document, 'click', (event: MouseEvent) => {
        if (
          event.button !== MOUSE_BUTTON.Right &&
          this.isOpen &&
          !this._elementRef.nativeElement.contains(event.target)
        ) {
          this.close();
        }
      }),
    );

    this.listenerFns.push(
      this._renderer.listen(this._elementRef.nativeElement, 'keyup.esc', () => {
        if (this.isOpen) {
          this.close();
        }
      }),
    );
  }

  get triggerValue(): string {
    if (this.selected) {
      return this.selected.viewValue;
    } else if (this.placeholder) {
      return this.placeholder;
    }
    return '';
  }
  readonly optionSelectionChange$: Observable<SelectionChange> = defer(() => {
    const options = this.options;

    if (options) {
      return options.changes.pipe(
        startWith(options),
        switchMap(() =>
          merge(...options.map((option) => option.selectionChange)),
        ),
      );
    }

    return this._ngZone.onStable.asObservable().pipe(
      take(1),
      switchMap(() => this.optionSelectionChange$),
    );
  });

  open(): void {
    if (!this.options || !this.options.length || this.disabled) {
      return;
    }

    this.isOpen = true;
  }

  toggle(): void {
    if (this.disabled) {
      this.isOpen = false;
      return;
    }
    this.isOpen = !this.isOpen;
  }

  _onChange: (value: DropdownValue) => void = () => {};

  close(): void {
    this.isOpen = false;
  }
  focus(): void {
    this.dropdownTrigger.nativeElement.focus();
  }
  focusNext(): void {
    const options = this.options.toArray();
    if (
      !options.some((option, index) => {
        if (option.focused && options[index + 1]) {
          options[index + 1].focus();
          return true;
        }
        return false;
      })
    ) {
      this.options.first.focus();
    }
  }
  focusPrev(): void {
    const options = this.options.toArray();
    if (
      !options.some((option, index) => {
        if (option.focused && options[index - 1]) {
          options[index - 1].focus();
          return true;
        }
        return false;
      })
    ) {
      this.options.last.focus();
    }
  }

  updateSelectedOption(value: DropdownValue) {
    this.options.forEach((opt) => opt.value !== value && opt.deselect());
    this.markSelectedValue();
  }

  markSelectedValue() {
    const initialValue = this.options.find(
      (option) => JSON.stringify(option.value) === JSON.stringify(this.value),
    );
    if (initialValue) {
      setTimeout(() => {
        this.selected = initialValue;
        this.selected.selected = true;
      });
    }
  }

  handleKeydown(event: KeyboardEvent) {
    const keyCode = getKeyCode(event);
    if (this.isOpen) {
      if (keyCode === 'ArrowDown') {
        event.preventDefault();
        this.focusNext();
      }
      if (keyCode === 'ArrowUp') {
        event.preventDefault();
        this.focusPrev();
      }
      if (keyCode === 'Escape') {
        this.close();
      }
    } else if (keyCode === 'ArrowDown') {
      this.open();
      event.preventDefault();
      this.options.first.focus();
    } else if (event.code === 'Space') {
      this.open();
      event.preventDefault();
      setTimeout(() => {
        this.listbox.nativeElement.focus();
      }, 0);
    }
  }

  initChangeListener(): void {
    this.options.changes
      .pipe(
        startWith(null),
        tap(() => this.markSelectedValue()),
        switchMap(() => this.optionSelectionChange$),
        takeUntil(this._destroy$),
      )
      .subscribe(
        ({
          option,
          value,
        }: {
          option: DropdownOptionComponent;
          value: DropdownValue;
        }) => {
          if (option instanceof DropdownOptionComponent) {
            this.options.forEach(
              (opt) => opt.value !== value && opt.deselect(),
            );
            this.selected = option;
            if (value) {
              this._onChange(value);
            }
            this.selectionChange.emit(value);
            this.viewValueChange.emit(option.viewValue);
            this.valueChange.emit(value);
            this.close();
            this.focus();
          }
        },
      );
  }

  ngAfterContentInit() {
    this.initChangeListener();
  }

  ngOnDestroy() {
    this.listenerFns.forEach((cleanupMethod) => cleanupMethod());
    this._destroy$.next();
    this._destroy$.complete();
  }
}
